import * as React from 'react';

import { getImageData } from 'lib/get-image-data';
import Image from 'lib/image-container';
import { useDetectPrint } from 'lib/use-detect-print';
import { MediaQueryWidth } from 'lib/use-media-query';
import useScrollbarWidth from 'lib/use-scrollbar-width';

import { Columns, ColumnsItem } from 'components/columns';
import { ColumnsSize } from 'components/columns/columns-item';
import { CtaBox, CtaBoxContent, CtaBoxHeader, CtaBoxText } from 'components/cta-box';
import PageSection from 'components/page/page-section';
import { Header2, Header3, P1 } from 'components/typography';

const PAGE_PADDING_SMALL = '16px';
const PAGE_PADDING_MEDIUM = '64px';
const PAGE_PADDING_LARGE = '108px';

type Props = {
  title?: string;
  subTitle?: string;
  columnLeft?: {
    align?: 'center' | 'start';
    contentBefore?: React.ReactNode;
    content?: React.ReactNode;
    size?: ColumnsSize;
  };
  columnRight?: {
    align?: 'center' | 'start';
    content: React.ReactNode;
    size?: ColumnsSize;
    title?: string;
  };
  image?: {
    src: string;
    objectPosition: string;
    inlinePosition?: string;
    hasLoadPriority?: boolean;
  };
};

const PageSectionTwoColumnsImageLeftColumn: React.FC<Props> = ({
  title,
  subTitle,
  columnLeft,
  columnRight,
  image,
  // eslint-disable-next-line complexity
}) => {
  const isPrinting = useDetectPrint();
  const scrollbarWidth = useScrollbarWidth(true);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const handleImageLoaded = () => setIsLoaded(true);
  const blurDataUrl = image ? getImageData(image.src).blurDataUrl : null;

  const leftImageBlock = image ? (
    <div
      className="page-section__picture"
      style={
        blurDataUrl
          ? {
              backgroundImage: `url("${blurDataUrl}")`,
              backgroundSize: 'cover',
              backgroundPosition: image.objectPosition,
            }
          : undefined
      }
    >
      <Image
        priority={image.hasLoadPriority}
        width={620}
        height={420}
        style={{
          objectFit: 'cover',
          objectPosition: image.objectPosition,
          opacity: blurDataUrl && !isLoaded ? 0 : 1,
          transition: 'opacity 0.5s',
        }}
        quality={75}
        src={image.src}
        alt=""
        onLoad={handleImageLoaded}
        sizes={`
                    (max-width: ${MediaQueryWidth['desktop-sm']}px) calc(100vw - 2 * ${PAGE_PADDING_SMALL} - ${scrollbarWidth}),
                    (max-width: ${MediaQueryWidth['tablet-lg']}px) calc(100vw - 2 * ${PAGE_PADDING_MEDIUM} - ${scrollbarWidth}),
                    (max-width: ${MediaQueryWidth['content-max']}px) calc(100vw - 2 * ${PAGE_PADDING_LARGE} - ${scrollbarWidth}),
                    1218px
                `}
      />
    </div>
  ) : null;

  return (
    <PageSection padding="block" inner={true} extraClass="tab-hero" color="transparent">
      <Columns
        // equal={true}
        hasMobileLayout={true}
        isMobileOrderInverted={image && image.inlinePosition !== 'start'}
      >
        <ColumnsItem verticalAlign="stretch" size={columnLeft?.size || ColumnsSize['1/2']}>
          <CtaBox align={columnLeft?.align ? columnLeft?.align : 'start'}>
            {title ? (
              <CtaBoxHeader>
                <Header2>{title}</Header2>
              </CtaBoxHeader>
            ) : null}
            {subTitle ? (
              <CtaBoxText>
                <P1 html={subTitle} as="p" />
              </CtaBoxText>
            ) : null}
            <CtaBoxContent width="stretch">
              {columnLeft?.contentBefore}
              {isPrinting ? null : leftImageBlock}
              {columnLeft?.content}
            </CtaBoxContent>
          </CtaBox>
        </ColumnsItem>

        <ColumnsItem size={columnRight?.size || ColumnsSize['1/2']}>
          <CtaBox align={columnRight?.align ? columnRight?.align : 'start'}>
            {columnRight?.title ? (
              <CtaBoxHeader>
                <Header3 as="p">{columnRight.title}</Header3>
              </CtaBoxHeader>
            ) : null}
            <CtaBoxContent width="stretch">{columnRight?.content}</CtaBoxContent>
          </CtaBox>
        </ColumnsItem>
      </Columns>
    </PageSection>
  );
};

export { PageSectionTwoColumnsImageLeftColumn };
